import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import safeStorage from '../../utils/safeStorage';
import UploadOverlay from '../UploadOverlay/UploadOverlay';

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });

// Request interceptor — reads token from localStorage at call time
api.interceptors.request.use((config) => {
  const token = safeStorage.getItem('authToken');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — turn 401/403 into a readable error
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Propagate with a human-readable message so callers can show it
      error.message = error.response.data?.error || 'Session expired. Please log in again.';
    }
    return Promise.reject(error);
  }
);

// Styles
const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    marginBottom: '24px'
  },
  headerTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#2d3748',
    marginBottom: '8px'
  },
  headerDescription: {
    color: '#718096',
    fontSize: '14px'
  },
  treeContainer: {
    background: 'white',
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  categoryNode: {
    background: '#f7fafc',
    border: '2px solid #2563eb',
    borderRadius: '8px',
    marginBottom: '12px',
    overflow: 'hidden'
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    background: '#2563eb',
    color: 'white',
    cursor: 'grab'
  },
  categoryHeaderDragging: {
    cursor: 'grabbing',
    boxShadow: '0 8px 24px rgba(37, 99, 235, 0.4)'
  },
  groupNode: {
    background: 'white',
    border: '1px solid #48bb78',
    borderRadius: '6px',
    marginBottom: '8px',
    marginLeft: '24px',
    marginRight: '12px'
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    background: '#28a745',
    color: 'white',
    cursor: 'grab',
    borderRadius: '6px 6px 0 0'
  },
  groupHeaderDragging: {
    cursor: 'grabbing',
    boxShadow: '0 4px 16px rgba(72, 187, 120, 0.4)'
  },
  topicNode: {
    background: '#fffaf0',
    border: '1px solid #ed8936',
    borderRadius: '4px',
    marginBottom: '6px',
    marginLeft: '24px',
    marginRight: '12px'
  },
  topicHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    background: '#f59e0b',
    color: 'white',
    cursor: 'grab',
    borderRadius: '4px 4px 0 0',
    fontSize: '14px'
  },
  topicHeaderDragging: {
    cursor: 'grabbing',
    boxShadow: '0 4px 12px rgba(237, 137, 54, 0.4)'
  },
  dragHandle: {
    marginRight: '12px',
    cursor: 'grab',
    opacity: 0.8,
    fontSize: '16px'
  },
  nodeTitle: {
    flex: 1,
    fontWeight: '600',
    fontSize: '15px'
  },
  nodeActions: {
    display: 'flex',
    gap: '8px'
  },
  actionButton: {
    background: 'rgba(255,255,255,0.85)',
    border: 'none',
    borderRadius: '4px',
    padding: '4px 8px',
    color: '#333',
    cursor: 'pointer',
    fontSize: '12px',
    transition: 'background 0.2s',
    fontWeight: '500'
  },
  expandButton: {
    background: 'none',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    padding: '4px',
    marginRight: '8px',
    fontSize: '14px'
  },
  childrenContainer: {
    padding: '12px 0 12px 0'
  },
  emptyChildren: {
    padding: '12px 24px',
    color: '#a0aec0',
    fontSize: '13px',
    fontStyle: 'italic'
  },
  form: {
    background: '#f7fafc',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px'
  },
  formRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px'
  },
  input: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    minHeight: '60px',
    resize: 'vertical'
  },
  button: {
    background: '#2563eb',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  buttonSecondary: {
    background: '#e2e8f0',
    color: '#4a5568'
  },
  buttonDanger: {
    background: '#fc8181',
    color: 'white'
  },
  buttonSmall: {
    padding: '6px 12px',
    fontSize: '12px'
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    background: 'rgba(255,255,255,0.9)',
    border: '2px dashed #cbd5e0',
    borderRadius: '6px',
    color: '#718096',
    cursor: 'pointer',
    fontSize: '13px',
    marginTop: '8px',
    marginLeft: '24px',
    marginRight: '12px',
    transition: 'all 0.2s'
  },
  error: {
    color: '#fc8181',
    background: '#fff5f5',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    background: 'white',
    borderRadius: '8px',
    padding: '24px',
    maxWidth: '500px',
    width: '90%'
  },
  instructions: {
    background: '#ebf8ff',
    border: '1px solid #bee3f8',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '20px',
    fontSize: '14px',
    color: '#2b6cb0'
  },
  messageList: {
    background: '#fff',
    borderTop: '1px solid #ed8936',
    padding: '12px'
  },
  messageItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '10px 12px',
    background: '#fefefe',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    marginBottom: '8px',
    fontSize: '14px'
  },
  messageContent: {
    flex: 1,
    lineHeight: '1.5'
  },
  messageMeta: {
    fontSize: '12px',
    color: '#718096',
    marginTop: '4px'
  },
  messageDeleteBtn: {
    background: '#fc8181',
    border: 'none',
    borderRadius: '4px',
    padding: '4px 8px',
    color: 'white',
    cursor: 'pointer',
    fontSize: '12px',
    flexShrink: 0
  },
  loadingMessages: {
    padding: '16px',
    textAlign: 'center',
    color: '#718096',
    fontSize: '13px'
  },
  noMessages: {
    padding: '16px',
    textAlign: 'center',
    color: '#a0aec0',
    fontSize: '13px',
    fontStyle: 'italic'
  }
};

// Tree Node Components
const TopicNode = ({ topic, index, onDelete, onDeleteMessage, api }) => {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const fetchMessages = async () => {
    setLoadingMessages(true);
    try {
      const res = await api.get(`/discussion-messages?topic_id=${topic.topic_id}`);
      setMessages(res.data);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
    setLoadingMessages(false);
  };

  const handleToggle = () => {
    if (!expanded && messages.length === 0) {
      fetchMessages();
    }
    setExpanded(!expanded);
  };

  const handleDeleteMessage = async (message) => {
    if (window.confirm(`Delete this message by ${message.username || 'Unknown'}?`)) {
      try {
        await api.delete(`/discussion-messages/${message.message_id}`);
        setMessages(messages.filter(m => m.message_id !== message.message_id));
        if (onDeleteMessage) onDeleteMessage(message);
      } catch (err) {
        console.error('Failed to delete message:', err);
        console.error('Error response:', err.response?.data);
        console.error('Error status:', err.response?.status);
        alert(`Failed to delete message: ${err.response?.data?.error || err.message}`);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Draggable draggableId={`topic-${topic.topic_id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...styles.topicNode,
            ...provided.draggableProps.style
          }}
        >
          <div
            {...provided.dragHandleProps}
            style={{
              ...styles.topicHeader,
              ...(snapshot.isDragging ? styles.topicHeaderDragging : {}),
              borderRadius: expanded ? '4px 4px 0 0' : '4px'
            }}
          >
            <button style={styles.expandButton} onClick={handleToggle}>
              {expanded ? '▼' : '▶'}
            </button>
            <span style={styles.dragHandle}>⋮⋮</span>
            <span style={styles.nodeTitle}>{topic.title}</span>
            <div style={styles.nodeActions}>
              <button
                style={styles.actionButton}
                onClick={() => onDelete(topic)}
                title="Delete topic"
              >
                
              </button>
            </div>
          </div>
          
          {expanded && (
            <div style={styles.messageList}>
              {loadingMessages ? (
                <div style={styles.loadingMessages}>Loading messages...</div>
              ) : messages.length === 0 ? (
                <div style={styles.noMessages}>No messages in this topic</div>
              ) : (
                messages.map(msg => (
                  <div key={msg.message_id} style={styles.messageItem}>
                    <div style={styles.messageContent}>
                      <div dangerouslySetInnerHTML={{ 
                        __html: msg.message_content?.substring(0, 200) + (msg.message_content?.length > 200 ? '...' : '') 
                      }} />
                      <div style={styles.messageMeta}>
                        By <strong>{msg.username || msg.nickname || 'Unknown'}</strong> • {formatDate(msg.created_at)}
                        {msg.parent_message_id && <span> • Reply</span>}
                      </div>
                    </div>
                    <button
                      style={styles.messageDeleteBtn}
                      onClick={() => handleDeleteMessage(msg)}
                      title="Delete message"
                    >
                      Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

const GroupNode = ({ group, index, topics, expanded, onToggle, onEdit, onDelete, onAddTopic, onDeleteTopic, api }) => {
  const groupTopics = topics.filter(t => t.group_id === group.group_id);
  
  return (
    <Draggable draggableId={`group-${group.group_id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...styles.groupNode,
            ...provided.draggableProps.style
          }}
        >
          <div
            {...provided.dragHandleProps}
            style={{
              ...styles.groupHeader,
              ...(snapshot.isDragging ? styles.groupHeaderDragging : {})
            }}
          >
            <button style={styles.expandButton} onClick={() => onToggle(group.group_id)}>
              {expanded ? '▼' : '▶'}
            </button>
            <span style={styles.dragHandle}>⋮⋮</span>
            
            {/* Group Image/Icon (display only - edit in modal) */}
            <div 
              style={{ 
                width: '32px', 
                height: '32px', 
                borderRadius: '6px', 
                marginRight: '8px',
                flexShrink: 0,
                overflow: 'hidden'
              }}
            >
              {group.has_image ? (
                <img 
                  src={`${API_URL}/discussion-groups/${group.group_id}/image`}
                  alt={group.group_name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <div style={{
                  width: '100%',
                  height: '100%',
                  background: '#28a745',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '16px'
                }}>
                  
                </div>
              )}
            </div>
            
            <span style={styles.nodeTitle}>
              {group.group_name}
              <span style={{ fontWeight: 'normal', opacity: 0.8, marginLeft: '8px', fontSize: '12px' }}>
                ({groupTopics.length} topics)
              </span>
            </span>
            <div style={styles.nodeActions}>
              <button style={styles.actionButton} onClick={() => onEdit(group)} title="Edit group">
                
              </button>
              <button style={styles.actionButton} onClick={() => onDelete(group)} title="Delete group">
                
              </button>
            </div>
          </div>
          
          {expanded && (
            <div style={styles.childrenContainer}>
              <Droppable droppableId={`topics-${group.group_id}`} type="TOPIC">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {groupTopics.length === 0 ? (
                      <div style={styles.emptyChildren}>No topics in this group</div>
                    ) : (
                      groupTopics.map((topic, idx) => (
                        <TopicNode
                          key={topic.topic_id}
                          topic={topic}
                          index={idx}
                          onDelete={onDeleteTopic}
                          api={api}
                        />
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
              <button style={styles.addButton} onClick={() => onAddTopic(group)}>
                + Add Topic
              </button>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

const CategoryNode = ({ category, index, groups, topics, expandedGroups, onToggleGroup, onEdit, onDelete, onAddGroup, onEditGroup, onDeleteGroup, onAddTopic, onDeleteTopic, api }) => {
  const [expanded, setExpanded] = useState(true);
  const categoryGroups = groups.filter(g => g.category_id === category.category_id);
  
  return (
    <Draggable draggableId={`category-${category.category_id}`} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...styles.categoryNode,
            ...provided.draggableProps.style
          }}
        >
          <div
            {...provided.dragHandleProps}
            style={{
              ...styles.categoryHeader,
              ...(snapshot.isDragging ? styles.categoryHeaderDragging : {})
            }}
          >
            <button style={styles.expandButton} onClick={() => setExpanded(!expanded)}>
              {expanded ? '▼' : '▶'}
            </button>
            <span style={styles.dragHandle}>⋮⋮</span>
            <span style={styles.nodeTitle}>
              {category.category_name}
              <span style={{ fontWeight: 'normal', opacity: 0.8, marginLeft: '8px', fontSize: '12px' }}>
                ({categoryGroups.length} groups)
              </span>
            </span>
            <div style={styles.nodeActions}>
              <button style={styles.actionButton} onClick={() => onEdit(category)} title="Edit category">
                
              </button>
              <button style={styles.actionButton} onClick={() => onDelete(category)} title="Delete category">
                
              </button>
            </div>
          </div>
          
          {expanded && (
            <div style={styles.childrenContainer}>
              <Droppable droppableId={`groups-${category.category_id}`} type="GROUP">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {categoryGroups.length === 0 ? (
                      <div style={styles.emptyChildren}>No groups in this category</div>
                    ) : (
                      categoryGroups.map((group, idx) => (
                        <GroupNode
                          key={group.group_id}
                          group={group}
                          index={idx}
                          topics={topics}
                          expanded={expandedGroups[group.group_id]}
                          onToggle={onToggleGroup}
                          onEdit={onEditGroup}
                          onDelete={onDeleteGroup}
                          onAddTopic={onAddTopic}
                          onDeleteTopic={onDeleteTopic}
                          api={api}
                        />
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
              <button style={styles.addButton} onClick={() => onAddGroup(category)}>
                + Add Group
              </button>
            </div>
          )}
        </div>
      )}
    </Draggable>
  );
};

// Form Modal Component
const FormModal = ({ title, fields, initialValues, onSubmit, onCancel }) => {
  const [values, setValues] = useState(initialValues || {});
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Validate required fields
    const requiredField = fields.find(f => f.required && !values[f.name]?.trim());
    if (requiredField) {
      setError(`${requiredField.label} is required`);
      return;
    }
    onSubmit(values);
  };

  return (
    <div style={styles.modal} onClick={onCancel}>
      <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginBottom: '16px' }}>{title}</h3>
        {error && <div style={styles.error}>{error}</div>}
        <form onSubmit={handleSubmit}>
          {fields.map(field => (
            <div key={field.name} style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
                {field.label} {field.required && '*'}
              </label>
              {field.type === 'textarea' ? (
                <textarea
                  style={styles.textarea}
                  value={values[field.name] || ''}
                  onChange={e => setValues({ ...values, [field.name]: e.target.value })}
                  placeholder={field.placeholder}
                />
              ) : (
                <input
                  style={styles.input}
                  type={field.type || 'text'}
                  value={values[field.name] || ''}
                  onChange={e => setValues({ ...values, [field.name]: e.target.value })}
                  placeholder={field.placeholder}
                />
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button type="submit" style={styles.button}>Save</button>
            <button type="button" style={{ ...styles.button, ...styles.buttonSecondary }} onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Group Edit Modal with Image Management
const GroupEditModal = ({ group, onSubmit, onCancel, onImageUpload, onImageDelete, api }) => {
  const [values, setValues] = useState({
    group_name: group.group_name || '',
    description: group.group_description || group.description || ''
  });
  const [error, setError] = useState('');
  const [hasImage, setHasImage] = useState(group.has_image);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!values.group_name?.trim()) {
      setError('Group Name is required');
      return;
    }
    onSubmit(values);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('image', file);
        await api.post(`/discussion-groups/${group.group_id}/image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setHasImage(true);
      } catch (err) {
        setError('Failed to upload image');
      }
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveImage = async () => {
    if (window.confirm('Remove the image from this group?')) {
      try {
        await api.delete(`/discussion-groups/${group.group_id}/image`);
        setHasImage(false);
      } catch (err) {
        setError('Failed to delete image');
      }
    }
  };

  return (
    <div style={styles.modal} onClick={onCancel}>
      <div style={{ ...styles.modalContent, maxWidth: '550px' }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginBottom: '16px' }}>Edit Group</h3>
        {error && <div style={styles.error}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
              Group Name *
            </label>
            <input
              style={styles.input}
              type="text"
              value={values.group_name}
              onChange={e => setValues({ ...values, group_name: e.target.value })}
            />
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '14px' }}>
              Description
            </label>
            <textarea
              style={styles.textarea}
              value={values.description}
              onChange={e => setValues({ ...values, description: e.target.value })}
            />
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
              Group Image
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '8px',
                overflow: 'hidden',
                border: '2px solid #e2e8f0',
                background: '#f7fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {hasImage ? (
                  <img
                    src={`${API_URL}/discussion-groups/${group.group_id}/image?t=${Date.now()}`}
                    alt={group.group_name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <span style={{ fontSize: '32px', opacity: 0.5 }}></span>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.buttonSmall }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? 'Uploading...' : hasImage ? 'Change Image' : 'Upload Image'}
                </button>
                {hasImage && (
                  <button
                    type="button"
                    style={{ ...styles.button, ...styles.buttonSmall, ...styles.buttonDanger }}
                    onClick={handleRemoveImage}
                  >
                    Remove Image
                  </button>
                )}
              </div>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button type="submit" style={styles.button}>Save</button>
            <button type="button" style={{ ...styles.button, ...styles.buttonSecondary }} onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Main Component
const DiscussionTreeAdmin = () => {
  const { isAdmin, token } = useAuth();

  // Keep the shared api instance in sync with the context token so that
  // it's always set even if localStorage was cleared independently.
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [topics, setTopics] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, groupRes, topicRes] = await Promise.all([
        api.get('/discussion-categories'),
        api.get('/discussion-groups'),
        api.get('/discussion-topics')
      ]);
      
      // Sort by display_order
      setCategories(catRes.data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));
      setGroups(groupRes.data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));
      setTopics(topicRes.data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle group expansion
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Handle drag end
  const onDragEnd = async (result) => {
    const { destination, source, type, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    try {
      if (type === 'CATEGORY') {
        const newCategories = [...categories];
        const [removed] = newCategories.splice(source.index, 1);
        newCategories.splice(destination.index, 0, removed);
        
        // Update display_order for all categories
        const updatedCategories = newCategories.map((cat, idx) => ({
          ...cat,
          display_order: idx
        }));
        setCategories(updatedCategories);
        
        // Save to server - API expects { categories: [{ category_id, display_order }] }
        await api.put('/discussion-categories/reorder', {
          categories: updatedCategories.map(c => ({ category_id: c.category_id, display_order: c.display_order }))
        });
      } else if (type === 'GROUP') {
        const sourceCategoryId = parseInt(source.droppableId.replace('groups-', ''));
        const destCategoryId = parseInt(destination.droppableId.replace('groups-', ''));
        const groupId = parseInt(draggableId.replace('group-', ''));
        
        // Get groups for source category
        const sourceGroups = groups.filter(g => g.category_id === sourceCategoryId);
        const destGroups = sourceCategoryId === destCategoryId 
          ? sourceGroups 
          : groups.filter(g => g.category_id === destCategoryId);
        
        // Find the moved group
        const movedGroup = groups.find(g => g.group_id === groupId);
        
        // Update local state
        let newGroups = groups.filter(g => g.group_id !== groupId);
        const updatedGroup = { ...movedGroup, category_id: destCategoryId };
        
        // Calculate new order within destination category
        const destGroupsWithoutMoved = destGroups.filter(g => g.group_id !== groupId);
        destGroupsWithoutMoved.splice(destination.index, 0, updatedGroup);
        
        // Update display_order
        destGroupsWithoutMoved.forEach((g, idx) => {
          g.display_order = idx;
        });
        
        // Merge back
        newGroups = [
          ...groups.filter(g => g.category_id !== destCategoryId && g.group_id !== groupId),
          ...destGroupsWithoutMoved
        ];
        
        setGroups(newGroups);
        
        // Save to server - API expects { groups: [{ group_id, display_order, category_id }] }
        await api.put('/discussion-groups/reorder', {
          groups: destGroupsWithoutMoved.map(g => ({ 
            group_id: g.group_id, 
            display_order: g.display_order,
            category_id: destCategoryId
          }))
        });
      } else if (type === 'TOPIC') {
        const destGroupId = parseInt(destination.droppableId.replace('topics-', ''));
        const topicId = parseInt(draggableId.replace('topic-', ''));
        
        const movedTopic = topics.find(t => t.topic_id === topicId);
        
        // Update local state
        let newTopics = topics.filter(t => t.topic_id !== topicId);
        const updatedTopic = { ...movedTopic, group_id: destGroupId };
        
        const destTopics = topics.filter(t => t.group_id === destGroupId && t.topic_id !== topicId);
        destTopics.splice(destination.index, 0, updatedTopic);
        
        destTopics.forEach((t, idx) => {
          t.display_order = idx;
        });
        
        newTopics = [
          ...topics.filter(t => t.group_id !== destGroupId && t.topic_id !== topicId),
          ...destTopics
        ];
        
        setTopics(newTopics);
        
        // Save to server - API expects { topics: [{ topic_id, display_order, group_id }] }
        await api.put('/discussion-topics/reorder', {
          topics: destTopics.map(t => ({ 
            topic_id: t.topic_id, 
            display_order: t.display_order,
            group_id: destGroupId
          }))
        });
      }
    } catch (err) {
      setError('Failed to save order');
      console.error(err);
      fetchData(); // Refresh to get correct state
    }
  };

  // CRUD operations
  const handleAddCategory = () => {
    setModal({
      title: 'Add Category',
      fields: [
        { name: 'category_name', label: 'Category Name', required: true, placeholder: 'Enter category name' },
        { name: 'category_description', label: 'Description', type: 'textarea', placeholder: 'Optional description' }
      ],
      onSubmit: async (values) => {
        try {
          await api.post('/discussion-categories', {
            ...values,
            display_order: categories.length
          });
          setModal(null);
          fetchData();
        } catch (err) {
          setError('Failed to create category');
        }
      }
    });
  };

  const handleEditCategory = (category) => {
    setModal({
      title: 'Edit Category',
      fields: [
        { name: 'category_name', label: 'Category Name', required: true },
        { name: 'category_description', label: 'Description', type: 'textarea' }
      ],
      initialValues: {
        category_name: category.category_name,
        category_description: category.category_description
      },
      onSubmit: async (values) => {
        try {
          await api.put(`/discussion-categories/${category.category_id}`, values);
          setModal(null);
          fetchData();
        } catch (err) {
          setError('Failed to update category');
        }
      }
    });
  };

  const handleDeleteCategory = async (category) => {
    if (window.confirm(`Delete "${category.category_name}"? This will also delete all groups and topics within it.`)) {
      try {
        await api.delete(`/discussion-categories/${category.category_id}`);
        fetchData();
      } catch (err) {
        setError('Failed to delete category');
      }
    }
  };

  const handleAddGroup = (category) => {
    const categoryGroups = groups.filter(g => g.category_id === category.category_id);
    setModal({
      title: `Add Group to "${category.category_name}"`,
      fields: [
        { name: 'group_name', label: 'Group Name', required: true, placeholder: 'Enter group name' },
        { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Optional description' }
      ],
      onSubmit: async (values) => {
        try {
          await api.post('/discussion-groups', {
            ...values,
            category_id: category.category_id,
            display_order: categoryGroups.length
          });
          setModal(null);
          fetchData();
        } catch (err) {
          setError('Failed to create group');
        }
      }
    });
  };

  const handleEditGroup = (group) => {
    setModal({
      type: 'editGroup',
      group: group,
      onSubmit: async (values) => {
        try {
          await api.put(`/discussion-groups/${group.group_id}`, values);
          setModal(null);
          fetchData();
        } catch (err) {
          setError('Failed to update group');
        }
      }
    });
  };

  const handleDeleteGroup = async (group) => {
    if (window.confirm(`Delete "${group.group_name}"? This will also delete all topics within it.`)) {
      try {
        await api.delete(`/discussion-groups/${group.group_id}`);
        fetchData();
      } catch (err) {
        setError('Failed to delete group');
      }
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleGroupImageUpload = async (groupId, file) => {
    setImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      await api.post(`/discussion-groups/${groupId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchData();
    } catch (err) {
      setError('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleGroupImageDelete = async (groupId) => {
    if (window.confirm('Remove the image from this group?')) {
      try {
        await api.delete(`/discussion-groups/${groupId}/image`);
        fetchData();
      } catch (err) {
        setError('Failed to delete image');
      }
    }
  };

  const handleAddTopic = (group) => {
    const groupTopics = topics.filter(t => t.group_id === group.group_id);
    setModal({
      title: `Add Topic to "${group.group_name}"`,
      fields: [
        { name: 'title', label: 'Topic Title', required: true, placeholder: 'Enter topic title' }
      ],
      onSubmit: async (values) => {
        try {
          await api.post('/discussion-topics', {
            ...values,
            group_id: group.group_id,
            display_order: groupTopics.length
          });
          setModal(null);
          fetchData();
        } catch (err) {
          setError('Failed to create topic');
        }
      }
    });
  };

  const handleDeleteTopic = async (topic) => {
    if (window.confirm(`Delete "${topic.title}"? This will also delete all messages within it.`)) {
      try {
        await api.delete(`/discussion-topics/${topic.topic_id}`);
        fetchData();
      } catch (err) {
        setError('Failed to delete topic');
      }
    }
  };

  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h2>Access Denied</h2>
          <p style={{ color: '#718096' }}>You need administrator privileges to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '60px', color: '#718096' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <UploadOverlay
        visible={imageUploading}
        message="Uploading image…"
        subtext="Processing your upload"
      />
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Discussion Tree Manager</h1>
        <p style={styles.headerDescription}>
          Drag and drop to reorder categories, groups, and topics. Changes are saved automatically.
        </p>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
          <button onClick={() => setError('')} style={{ marginLeft: '12px', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      <div style={styles.instructions}>
        <strong>Tips:</strong>
        <ul style={{ margin: '8px 0 0 20px', padding: 0 }}>
          <li>Drag items by the ⋮⋮ handle to reorder</li>
          <li>Drag groups between categories to move them</li>
          <li>Drag topics between groups to move them</li>
          <li>Click ▶/▼ to expand or collapse sections</li>
        </ul>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <button style={styles.button} onClick={handleAddCategory}>
          + Add Category
        </button>
      </div>

      <div style={styles.treeContainer}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="categories" type="CATEGORY">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {categories.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>
                    No categories yet. Click "Add Category" to create one.
                  </div>
                ) : (
                  categories.map((category, index) => (
                    <CategoryNode
                      key={category.category_id}
                      category={category}
                      index={index}
                      groups={groups}
                      topics={topics}
                      expandedGroups={expandedGroups}
                      onToggleGroup={toggleGroup}
                      onEdit={handleEditCategory}
                      onDelete={handleDeleteCategory}
                      onAddGroup={handleAddGroup}
                      onEditGroup={handleEditGroup}
                      onDeleteGroup={handleDeleteGroup}
                      onAddTopic={handleAddTopic}
                      onDeleteTopic={handleDeleteTopic}
                      api={api}
                    />
                  ))
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {modal && modal.type === 'editGroup' && (
        <GroupEditModal
          group={modal.group}
          onSubmit={modal.onSubmit}
          onCancel={() => setModal(null)}
          api={api}
        />
      )}

      {modal && modal.type !== 'editGroup' && (
        <FormModal
          title={modal.title}
          fields={modal.fields}
          initialValues={modal.initialValues}
          onSubmit={modal.onSubmit}
          onCancel={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default DiscussionTreeAdmin;
